import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useSetUserProperties } from 'shared/hooks/use-set-user-properties';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import {
  OnboardingWorkSituations,
  PageEvents,
  UserPropertiesKeys,
} from 'shared/services/tracking';

import { OnboardingHeader } from '../onboarding-header';
import { OnboardingSideImage } from '../onboarding-side-image';
import { ProgressBar } from '../progress-bar';
import * as Styled from './work-situation.style';

const WORK_IMAGE_URL = '/images/onboarding/work-situation.png';

export type WorkSituationProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  onSubmit: (value: OnboardingWorkSituations) => void;
};

export const WorkSituation: React.FC<WorkSituationProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  onSubmit,
}) => {
  useTrackPageVisitEvent(PageEvents.OnboardingWorkSituation, undefined);
  const setUserProperties = useSetUserProperties();
  const { t } = useTranslation();
  const workSituations = [
    {
      value: OnboardingWorkSituations.Student,
      label: t('forms.onboarding.work-situation.student'),
    },
    {
      value: OnboardingWorkSituations.Employed,
      label: t('forms.onboarding.work-situation.employed'),
    },
    {
      value: OnboardingWorkSituations.Freelancer,
      label: t('forms.onboarding.work-situation.freelancer'),
    },
    {
      value: OnboardingWorkSituations.Solopreneur,
      label: t('forms.onboarding.work-situation.solopreneur'),
      description: t('forms.onboarding.work-situation.solopreneur.description'),
    },
    {
      value: OnboardingWorkSituations.SmallBusinessOwner,
      label: t('forms.onboarding.work-situation.small-business-owner'),
      description: t(
        'forms.onboarding.work-situation.small-business-owner.description',
      ),
    },
    {
      value: OnboardingWorkSituations.MediumBusinessOwner,
      label: t('forms.onboarding.work-situation.medium-business-owner'),
      description: t(
        'forms.onboarding.work-situation.medium-business-owner.description',
      ),
    },
    {
      value: OnboardingWorkSituations.LargeBusinessOwner,
      label: t('forms.onboarding.work-situation.large-business-owner'),
      description: t(
        'forms.onboarding.work-situation.large-business-owner.description',
      ),
    },
    {
      value: OnboardingWorkSituations.Other,
      label: t('forms.onboarding.work-situation.other'),
    },
  ];

  const _onSubmit = (value: OnboardingWorkSituations) => {
    setUserProperties({ [UserPropertiesKeys.WorkSituation]: value });
    onSubmit(value);
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />
        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Form>
            <Typography variant="h4" component="h1">
              {t('forms.onboarding.work-situation.title')}
            </Typography>

            <Styled.List>
              {workSituations.map(({ value, label, description }) => (
                <Styled.ListItem key={value} onClick={() => _onSubmit(value)}>
                  <Styled.ListItemTextBlock>
                    <Typography variant="subtitle2">{label}</Typography>

                    {!!description && (
                      <Styled.ListItemDescription>
                        {description}
                      </Styled.ListItemDescription>
                    )}
                  </Styled.ListItemTextBlock>

                  <Styled.ChevronContainer>
                    <Icon icon={ChevronLeft} />
                  </Styled.ChevronContainer>
                </Styled.ListItem>
              ))}
            </Styled.List>
          </Styled.Form>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>

      <Styled.RightPanelWrapper>
        <OnboardingSideImage
          imageUrl={WORK_IMAGE_URL}
          maxImageWidth={318}
          relativeImage
        />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
